import React, { useEffect, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { MediaComponent, SearchInput } from 'components/index.js'
import { DragDropModal } from 'components/Modals'
import { updateSearchAction } from 'store/actions/header.action'
import { Dialog, DialogContent } from '@components/ui/dialog'
import { useRouter } from 'next/router'
import { Button } from '@components/ui/button'
import { UploadIcon } from 'lucide-react'

const SelectMediaModal = ({ open, onClose, selectMedia }) => {
  const { t } = useTranslation('common')

  const dispatch = useDispatch()
  const router = useRouter()

  const user = useSelector((state) => state.auth.user)
  const [dragDropVisible, setDragDropVisible] = useState(false)
  const [search, setSearch] = useState('')

  let timeoutId
  useEffect(() => {
    if (timeoutId) clearTimeout(timeoutId)
    if (search) {
      timeoutId = setTimeout(() => {
        dispatch(updateSearchAction(search))
      }, 150)
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [search])

  const closeModal = () => {
    // Clear specific query parameters
    const query = { ...router.query }
    delete query.filter // Explicitly remove the filter key

    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true } // Avoid full page reload
    )

    onClose() // Trigger modal close logic
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && closeModal()}>
      <DialogContent
        isCloseVisible={true}
        className="w-[90vw] h-[96%] max-w-[1940px] gap-4 !justify-start p-4 md:p-6 lg:p-10 !max-h-[96vh] overflow-hidden"
        style={{ minHeight: '80vh' }}
      >
        {dragDropVisible && <DragDropModal libraryModal={true} onClose={() => setDragDropVisible(false)} />}

        <div className="flex min-w-[85vw]  max-w-[1940px] flex-col items-center justify-center px-2 md:px-1 !mt-0 gap-4">
          {/* Desktop Layout - Single Row */}
          <div className="hidden lg:flex items-center justify-between w-full gap-6">
            <div className="text-[#101828] font-black text-3xl flex-shrink-0">{t('common.library')}</div>

            <div className="flex-1 max-w-2xl mx-8">
              <SearchInput
                placeholder={t('common.search-library')}
                className="w-full"
                style={{
                  padding: '12px 20px',
                  height: '48px',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: '12px',
                  border: '2px solid #e5e7eb',
                  fontSize: '16px',
                }}
                onChange={(e) => {
                  setSearch(e.target.value)
                }}
                value={search}
              />
            </div>

            {user && (
              <Button
                className="flex items-center gap-2 px-6 py-3 bg-black text-white hover:bg-gray-800 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 shadow-lg flex-shrink-0"
                onClick={() => setDragDropVisible(true)}
              >
                <UploadIcon className="w-5 h-5" />
                <span>{t('common.upload')}</span>
              </Button>
            )}
          </div>

          {/* Tablet Layout - Title and Upload in one row, Search below */}
          <div className="hidden md:flex lg:hidden flex-col w-full gap-4">
            <div className="flex items-center justify-between w-full">
              <div className="text-[#101828] font-black text-2xl md:text-3xl">{t('common.library')}</div>
              {user && (
                <Button
                  className="flex items-center gap-2 px-4 py-2 bg-black text-white hover:bg-gray-800 rounded-lg font-semibold transition-all duration-200"
                  onClick={() => setDragDropVisible(true)}
                >
                  <UploadIcon className="w-4 h-4" />
                  <span>{t('common.upload')}</span>
                </Button>
              )}
            </div>

            <div className="w-full flex justify-center">
              <div className="w-full max-w-lg">
                <SearchInput
                  placeholder={t('common.search-library')}
                  className="w-full"
                  style={{
                    padding: '12px 18px',
                    height: '46px',
                    width: '100%',
                    boxSizing: 'border-box',
                    borderRadius: '10px',
                    border: '2px solid #e5e7eb',
                  }}
                  onChange={(e) => {
                    setSearch(e.target.value)
                  }}
                  value={search}
                />
              </div>
            </div>
          </div>

          {/* Mobile Layout - Stacked with improved styling */}
          <div className="flex md:hidden flex-col w-full gap-4">
            <div className="flex items-center justify-between w-full">
              <div className="text-[#101828] font-black text-xl">{t('common.library')}</div>
              {user && (
                <div
                  className="flex gap-2 items-center px-4 py-2 rounded-lg border-2 border-gray-200 cursor-pointer hover:bg-gray-50 transition-all duration-200 active:scale-95 bg-white shadow-sm"
                  onClick={() => setDragDropVisible(true)}
                >
                  <UploadIcon className="w-4 h-4 text-gray-700" />
                  <span className="text-sm font-semibold text-gray-700">{t('common.upload')}</span>
                </div>
              )}
            </div>

            <div className="w-full">
              <SearchInput
                placeholder={t('common.search-library')}
                className="w-full"
                style={{
                  padding: '12px 16px',
                  height: '44px',
                  width: '100%',
                  boxSizing: 'border-box',
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                }}
                onChange={(e) => {
                  setSearch(e.target.value)
                }}
                value={search}
              />
            </div>
          </div>
        </div>

        <div className="overflow-auto px-2 min-w-[85vw]  max-w-[1940px] md:px-1 pr-3 media-upload-scrollbar flex-1 min-h-0">
          <MediaComponent
            onClickMedia={(media) => {
              selectMedia({ full_url: media })
              closeModal()
            }}
            defaultFilter={user ? 'My Results' : 'Trending'}
            filterClassNames={'items-center justify-start'}
            classNames={'!mt-0 pr-[30px]'}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default SelectMediaModal
